import { computed } from 'vue';
import useDynamicComponent from '~/composables/useDynamicComponent';
import formatUtils from '~/utils/formatUtils';
import type { AsyncDataRequestStatus } from '#app';
import type { IDynamicComponentConfig } from '~~/configs/dynamic.components.config';
import type { ICmsContentTypes } from '~~/types/_cms-types';

export interface IDynamicComponentState {
  data?: ICmsContentTypes | Error;
  status?: Ref<AsyncDataRequestStatus> | string;
  error?: object;
  component?: Component;
  props?: object;
  staticData?: Record<string, unknown>;
}

type ItemData = Record<string, unknown>;
type MappedFields = { item: Record<string, unknown> }[];

/* Used to fetch and manage dynamic component data
  collection: Collection name to fetch data from > this is the "block" we want to fetch data for
  item: Identifier of the item to fetch
  relationalFieldName: Field name used to resolve relationships between "article" and the "block"
*/
export default async function useDynamicComponentData(
  collection: string,
  item: number,
  relationalFieldName: string,
  staticData?: Record<string, unknown>,
): Promise<IDynamicComponentState> {
  const cms = useCms();
  const { toCamelCase, objectKeysToCamelCase } = formatUtils();

  // Generate a unique cache key for this data
  const cacheKey = `${relationalFieldName}-${item}-${collection}`;

  // Dynamically resolve the component configuration for the collection
  const componentDefinition = computed((): IDynamicComponentConfig | undefined => {
    if (collection) {
      return useDynamicComponent(toCamelCase(collection));
    }
  });

  // Extract the fields to fetch from the component definition
  const fields = componentDefinition.value?.fields ? componentDefinition.value.fields : undefined;

  // Reactive state object to hold the fetched data and metadata
  const state: IDynamicComponentState = reactive({
    data: undefined,
    status: undefined,
    error: undefined,
    component: componentDefinition?.value?.component
      ? shallowRef(componentDefinition.value.component)
      : undefined, // Use the resolved component if available
    props: componentDefinition.value?.props, // Props for the component
    staticData: staticData,
  });

  // Function to fetch the required data asynchronously
  async function fetchData() {
    /*
        This function is responsible for fetching the content of the relational item.
        We only fetch content for published articles or items, using the permissions
        defined in the associated collection.
      */
    const formattedFields: MappedFields = [
      'id', // NEEDED FOR BACKEND
      'collection', // NEEDED FOR BACKEND
      {
        item: {
          [collection]: fields,
        },
      },
    ];

    return cms.readItem(relationalFieldName, item, { fields: formattedFields });
  }

  // If fields are defined, check cache or fetch fresh data
  if (fields) {
    const cachedData = useNuxtData(cacheKey); // Check if data exists in cache

    if (cachedData?.data?.value) {
      // If cached data exists, populate the state
      state.data = (
        cachedData?.data?.value?.item
          ? objectKeysToCamelCase(cachedData.data.value.item)
          : undefined
      ) as ICmsContentTypes;
      state.error = undefined;
      state.status = 'success';
    } else {
      try {
        const data = await fetchData();

        // cache it
        if (data) {
          cachedData.data.value = data;
        }

        state.data = (
          data?.item ? objectKeysToCamelCase(data.item as ItemData) : undefined
        ) as ICmsContentTypes;
        state.error = undefined;
        state.status = 'success';
      } catch (error) {
        state.error = error;
        state.status = 'error';
      }
    }
  } else if (componentDefinition.value?.noData) {
    state.status = 'success';
  } else {
    console.warn(`No fields defined for ${collection}.`);
    state.status = 'error';
  }

  // Return the final state object
  return state;
}
